import { DEFAULT_MODEL_ID, SEED_MODELS } from '@/versions/v1/shared/chat-types';
import type { BedrockModelHealthEntry } from '@/versions/v1/shared/admin-seed';

export type AgentKitId = 'claude' | 'google';

export type AppModel = {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  kit: AgentKitId;
  contextWindowTokens: number;
};

const CONTEXT_WINDOWS: Record<string, number> = {
  'claude-opus-4-8': 1_000_000,
  'claude-sonnet-4-6': 200_000,
  'claude-haiku-4-5': 200_000,
  'gemini-3.5-flash': 1_048_576,
  'gemini-3.1-pro-preview': 2_097_152,
  'gemini-3.1-flash-lite': 1_048_576,
};

export const SEED_APP_MODELS: AppModel[] = SEED_MODELS.map((m) => ({
  id: m.id,
  label: m.label,
  shortLabel: m.shortLabel,
  description: m.description,
  kit: m.provider === 'google' ? 'google' : 'claude',
  contextWindowTokens: CONTEXT_WINDOWS[m.id] ?? 200_000,
}));

export const CLAUDE_CATALOG_MODELS = SEED_APP_MODELS.filter((m) => m.kit === 'claude');
export const GOOGLE_CATALOG_MODELS = SEED_APP_MODELS.filter((m) => m.kit === 'google');

export const CLAUDE_MODELS_CONFIG_SLUG = 'claude-models';
export const GOOGLE_MODELS_CONFIG_SLUG = 'google-models';
export const AI_MODELS_CONFIG_SLUG = 'ai-models';

export function formatContextWindow(tokens: number): string {
  if (tokens >= 1_000_000) {
    const millions = tokens / 1_000_000;
    return `${Number.isInteger(millions) ? millions : millions.toFixed(1).replace(/\.0$/, '')}M`;
  }
  return `${Math.round(tokens / 1000)}K`;
}

export function getModel(id: string): AppModel | undefined {
  return SEED_APP_MODELS.find((m) => m.id === id);
}

export function resolvedFallbackLabel(
  disablingKit: AgentKitId,
  platformDefault: string | null,
  appDefaultModelId: string | null,
): string {
  const survives = (modelId: string | null | undefined): string | null => {
    if (!modelId) return null;
    const row = SEED_APP_MODELS.find((m) => m.id === modelId);
    return row && row.kit !== disablingKit ? row.label : null;
  };
  const fromPlatformDefault = survives(platformDefault);
  if (fromPlatformDefault) return fromPlatformDefault;
  const fromAppDefault = survives(appDefaultModelId);
  if (fromAppDefault) return fromAppDefault;
  const otherKitModel = SEED_APP_MODELS.find((m) => m.kit !== disablingKit);
  if (otherKitModel) return otherKitModel.label;
  return 'another available model';
}

export type AllowlistConfigValue = {
  allowedIds: string[] | null;
  defaultId: string | null;
};

export type KitAllowlistSeed = {
  allowedIds: string[] | null;
  kitEnabled: boolean;
  maxLlmCalls?: number | null;
};

export type AiModelsSeed = {
  platformDefaultModelId: string | null;
  webSearch: { claude: boolean; google: boolean };
  claude: KitAllowlistSeed;
  google: KitAllowlistSeed;
  modelHealth?: ReadonlyMap<string, BedrockModelHealthEntry>;
};

export { DEFAULT_MODEL_ID };
